<template>
  <div class="expanded-tip-container" ref="expandedTipContainer" v-show="expandedTooltips.isActive">
    <div class="background-container" @click="hideExpandedTooltip()"></div>
    <div class="arrow" :style="arrowTransformation"></div>
    <div class="arrow-shadow" :style="arrowTransformation"></div>
    <div class="expanded-tip-wrapper" :style="expandedTipContainerTransformation">
      <div class="title">
        <i class="material-icons close" @click="hideExpandedTooltip()">close</i>
        <span v-html="title"></span>
      </div>
      <div class="content">
        <span v-html="content"></span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import tooltipContent from '@/i18n/en/tooltip-content';
import _ from 'lodash';

export default {
  name: 'tooltip-expanded',
  data() {
    return {
      expandedTipContainerTransformation: '',
      arrowTransformation: ''
    }
  },
  computed: {
    ...mapState({
      expandedTooltips: state => state.tooltips.expanded,
      insuranceType: state => state.insuranceType.key
    }),
    key() {
      return this.expandedTooltips.key;
    },
    title() {
      if (_.get(this.expandedTooltips, 'key')) {
        let key = this.expandedTooltips.key;
        if (_.get(tooltipContent, [this.insuranceType, key, 'title'])) {
          return tooltipContent[this.insuranceType][key].title;
        } else if (_.get(tooltipContent, ['default', key, 'title'])) {
          return tooltipContent.default[key].title;
        } else {
          // Tooltip key doesn't exist in custom tooltip text or default text
          return '';
        }
      } else {
        // Tooltip key isn't set
        return '';
      }
    },
    content() {
      if (_.get(this.expandedTooltips, 'key')) {
        let key = this.expandedTooltips.key;
        if (_.get(tooltipContent, [this.insuranceType, key, 'content'])) {
          return tooltipContent[this.insuranceType][key].content;
        } else if (_.get(tooltipContent, ['default', key, 'content'])) {
          return tooltipContent.default[key].content;
        } else {
          // Tooltip key doesn't exist in custom tooltip text or default text
          return '';
        }
      } else {
        // Tooltip key isn't set
        return '';
      }
    }
  },
  methods: {
    hideExpandedTooltip() {
      this.$store.commit('HIDE_TOOLTIP_EXPANDED');
    },
    expandedTipTransform() {
      // NOTE: If the content in the expanded tooltip includes an image, a min-height must be set on the image
      // to ensure the tooltip and arrow are positioned correctly.
      // Ex: <img style="min-height: 0px;" src="mytooltipimage.jpg" />

      // The nextTick call is needed to ensure the DOM is updated with the title and content,
      // and to ensure the tooltip width and height is correct before positioning
      this.$nextTick(() => {
        if (this.$refs.expandedTipContainer) {
          if (_.get(this.expandedTooltips, 'containerPosition')) {
            // Define variables for expandedTooltips.containerPosition and expandedTipContainer
            let elemPosition = this.expandedTooltips.containerPosition;
            let tooltipPosition = this.$refs.expandedTipContainer;

            // Calculate vertical midpoint of trigger element
            let elemVertMidPoint = elemPosition.top + (elemPosition.height / 2);

            // Calculate position for tooltip
            let tooltipLeft = elemPosition.left - (tooltipPosition.clientWidth + 10);
            let tooltipTop = this.expandedTooltips.scrollDelta + elemVertMidPoint - (tooltipPosition.clientHeight / 2);

            // Calculate position for arrow
            let arrowLeft = tooltipLeft + 10;
            let arrowTop = tooltipTop - (tooltipPosition.clientHeight / 2) + 10;

            // Set the transformation
            this.expandedTipContainerTransformation = `transform: translate(${tooltipLeft}px, ${tooltipTop}px);`;
            this.arrowTransformation = `transform: translate(${arrowLeft}px, ${arrowTop}px);`;
          } else {
            this.expandedTipContainerTransformation = '';
            this.arrowTransformation = '';
          }
        } else {
          this.expandedTipContainerTransformation = '';
          this.arrowTransformation = '';
        }
      });
    }
  },
  watch: {
    // Need to watch the key so the expandedTipTransform method can be triggered
    key() {
      this.expandedTipTransform();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

/* General expanded tooltip styling */
.expanded-tip-container {
  position: absolute;
  z-index: 10;
  left: 0;
  top: 0;
  font-size: $sfi-xxs-font-size;

  .background-container {
    position: fixed;
    z-index: -1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
  }

  .expanded-tip-wrapper {
    .title {
      background-color: $light-gray;
      padding: 14px;
      font-weight: $global-weight-semibold;

      .close {
        position: absolute;
        border-radius: 50%;
        color: $white;
        background-color: $black;
        cursor: pointer;
      }
    }

    .content {
      overflow: auto;
      background-color: $white;
      padding: 14px;
      font-weight: normal;
    }
  }
}

/* Mobile expanded tooltip styling */
@include breakpoint(small only) {
  .expanded-tip-container {
    position: fixed;
    width: 85%;
    min-width: 320px;
    max-width: 400px;
    height: 100%;

    .background-container {
      background-color: rgba(0, 0, 0, 0.25);
    }

    .expanded-tip-wrapper {
      height: 100%;
      transform: translate(0, 0) !important;
      box-shadow: 0 0 4px 0 rgba(51, 51, 51, 0.25);

      .title {
        padding-right: calc(14px + 24px);

        .close {
          top: 12px;
          right: 12px;
        }
      }

      .content {
        height: 100%;
      }
    }
  }
}

/* Desktop and tablet expanded tooltip styling */
@include breakpoint(medium) {
  .expanded-tip-container {
    /* Ensure 4px spacing between tooltip and tooltip trigger after calculations */
    left: -4px;
    top: 0;

    .background-container {
      background-color: transparent;
    }

    .arrow {
      position: absolute;
      width: 0;
      height: 0;
      bottom: 0;
      right: 0;
      z-index: 1;
      border-top: 10px solid transparent;
      border-bottom: 10px solid transparent;
      border-left: 10px solid $white;
    }

    .arrow-shadow {
      position: absolute;
      width: 0;
      height: 0;
      bottom: -1px;
      right: -3px;
      border-top: 11px solid transparent;
      border-bottom: 11px solid transparent;
      border-left: 11px solid rgba(51, 51, 51, 0.25);
      filter: blur(1px);
    }

    .expanded-tip-wrapper {
      max-width: 250px;
      border-radius: 6px;
      box-shadow: 0 0 4px 0 rgba(51, 51, 51, 0.5);

      .title {
        border-radius: 6px 6px 0 0;

        .close {
          top: -10px;
          right: -10px;
        }
      }

      .content {
        border-radius: 0 0 6px 6px;
        max-height: 300px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/shared/tooltip-expanded.vue